import { captureError } from "./capture-error"

// A syntactically valid wss URL whose host never resolves, so the probe never
// opens a real connection. A broken/hijacked constructor throws synchronously here.
const PROBE_URL = "wss://ws-probe.invalid"

type WebSocketCtor = typeof WebSocket

export function isConstructibleWebSocket(ws: unknown): ws is WebSocketCtor {
	if (typeof ws !== "function") return false
	try {
		const socket = new (ws as WebSocketCtor)(PROBE_URL)
		socket.close()
		return true
	} catch {
		return false
	}
}

export type WebSocketResolution =
	| { status: "ok" }
	| { status: "repaired"; ws: WebSocketCtor }
	| { status: "unrecoverable" }

/**
 * Only falls back to `getNative` when `current` is broken, so correct
 * instrumentation wrappers (Sentry, PostHog) are left untouched.
 */
export function resolveWebSocket(
	current: unknown,
	getNative: () => unknown,
): WebSocketResolution {
	if (isConstructibleWebSocket(current)) return { status: "ok" }
	const native = getNative()
	if (isConstructibleWebSocket(native))
		return { status: "repaired", ws: native }
	return { status: "unrecoverable" }
}

// A fresh iframe realm keeps the untouched native constructor when a page-context
// script has clobbered window.WebSocket. Keep the iframe attached so its realm stays alive.
function grabNativeWebSocket(): unknown {
	try {
		const iframe = document.createElement("iframe")
		iframe.style.display = "none"
		document.body.appendChild(iframe)
		return (iframe.contentWindow as unknown as { WebSocket?: unknown })
			?.WebSocket
	} catch {
		return undefined
	}
}

export function ensureWorkingWebSocket(): void {
	if (typeof window === "undefined") return
	const result = resolveWebSocket(window.WebSocket, grabNativeWebSocket)
	if (result.status === "ok") return
	if (result.status === "repaired") {
		window.WebSocket = result.ws
		captureError(new Error("Restored a broken window.WebSocket constructor"), {
			domain: "zero",
			action: "sanitize websocket global",
		})
		return
	}
	// Unrecoverable means an extension or policy blocks WebSocket in every realm
	// of this browser; nothing on our side can fix it, so it is not reported.
}
